// Root Agent — deployed to Cloud Run as `tg-root-agent-service`. ADK `/run_sse`
// streaming client, the widget's own copy of the PIMS assistant page's client
// (src/pages/Menu/TgPimsAssistant/api/chat.js), so the embeddable widget only
// imports from its own folder.
const ROOT_AGENT_URL =
    process.env.REACT_APP_ROOT_AGENT_URL ||
    "https://tg-root-agent-service-1050393382437.us-central1.run.app";

// ADK derives the app name from the agent directory (`services/root_agent/`).
const APP_NAME = "root_agent";

// Every call carries the website chatbot's token (widgetToken.js) and the
// header that tells the Root Agent to check it as one, instead of as a
// Firebase sign-in.
const authHeaders = (token) => ({
    Authorization: `Bearer ${token}`,
    "X-Chat-Client": "website-chatbot",
});

// The Root Agent records each conversation and stores the page it happened on,
// which it reads from the Referer. Browsers send only the site for a
// cross-site call unless asked for the full address.
const REFERRER_POLICY = "no-referrer-when-downgrade";

// True when text breaks off mid-sentence — no closing punctuation and no line
// break. The video path streams its AI blurb as fragments like "這支" and
// "影片的「…」章節", which end this way; a self-contained block (a table, the
// "no video, using text instead" notice, a finished paragraph) does not.
const ENDS_MID_SENTENCE = /[^\s。．.!?！？:：\n]$/;

// Appends one content block to the answer being assembled. Everything lands in
// ONE string so MessageRenderer parses it in a single pass; a block either
// continues the previous sentence or starts a new paragraph.
export function appendBlock(existing, block) {
    if (!existing) return block;
    const continuesSentence =
        ENDS_MID_SENTENCE.test(existing) && !block.startsWith("\n");
    return continuesSentence ? existing + block : `${existing}\n\n${block}`;
}

// Opens the agent's session under the widget's own session id, so the agent's
// session and the saved conversation are one conversation. ADK refuses to
// create a session that already exists; that is the agent still having it,
// which is what we want, so it counts as success.
export async function createRootAgentSession(token, userId, sessionId) {
    const response = await fetch(
        `${ROOT_AGENT_URL}/apps/${APP_NAME}/users/${userId}/sessions/${sessionId}`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders(token) },
            referrerPolicy: REFERRER_POLICY,
            body: JSON.stringify({}),
        },
    );
    const alreadyExists = response.status === 400 || response.status === 409;
    if (!response.ok && !alreadyExists) {
        throw new Error(`Failed to create chat session: ${response.status}`);
    }
    return sessionId;
}

/**
 * Stream one turn from the Root Agent.
 *
 * A few `stateDelta.stage` events, then one or more content events, each a
 * complete block of text (`partial: true` only means more blocks follow). There
 * is no turn_complete event: the turn ends when the stream closes.
 *
 * handlers: { onStageChange, onBlock, onSources, onVideos, onWorker, onDone, onError }
 *
 * includeVideo — answer with videos when the library has a match; on a miss
 *   the agent sends a "no video, answering in text" block, then the text.
 * { model, thinking, verbosity } — sent every turn as concrete values:
 *   stateDelta merges, so an omitted key would keep the previous turn's.
 */
export async function streamRootAgentMessage(
    token,
    userId,
    sessionId,
    message,
    handlers,
    language,
    includeVideo,
    { model, thinking, verbosity } = {},
) {
    let response;
    try {
        response = await fetch(`${ROOT_AGENT_URL}/run_sse`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders(token) },
            referrerPolicy: REFERRER_POLICY,
            body: JSON.stringify({
                appName: APP_NAME,
                userId,
                sessionId,
                newMessage: { role: "user", parts: [{ text: message }] },
                streaming: true,
                // One stateDelta object for every flag: two spread
                // `{ stateDelta }` objects would overwrite each other.
                // `include_video` is snake_case to match the backend, and an
                // explicit false, never omitted, for the merge reason above.
                stateDelta: {
                    ...(language ? { user_lang: language } : {}),
                    include_video: includeVideo === true,
                    model,
                    thinking,
                    verbosity,
                },
            }),
        });
    } catch (error) {
        handlers.onError(error instanceof Error ? error : new Error(String(error)));
        return;
    }

    if (!response.ok || !response.body) {
        handlers.onError(new Error(`Agent request failed: ${response.status}`));
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const blocks = [];

    let stage = null;
    function setStage(next) {
        if (stage === next) return;
        stage = next;
        handlers.onStageChange(next);
    }

    function handleEvent(event) {
        const delta = event.actions?.stateDelta;
        if (delta?.stage) setStage(delta.stage);

        // FAQ-path answers cite sources inline as [1], [2]...; array position
        // is the citation number.
        if (Array.isArray(delta?.sources) && delta.sources.length > 0) {
            handlers.onSources?.(
                delta.sources.map((s, i) => ({ ...s, index: i + 1 })),
            );
        }

        // Video answers: structured cards for the same videos the text lists.
        if (Array.isArray(delta?.videos) && delta.videos.length > 0) {
            handlers.onVideos?.(delta.videos);
        }

        // Which backend answered ("gpt" for the live website search).
        const worker = event.output?.worker;
        if (worker) handlers.onWorker?.(worker);

        const parts = event.content?.parts;
        if (!Array.isArray(parts)) return;
        const text = parts.map((p) => p.text ?? "").join("");
        if (!text) return;
        blocks.push(text);
        handlers.onBlock(text);
    }

    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";

            for (const line of lines) {
                if (!line.startsWith("data:")) continue;
                const payload = line.slice("data:".length).trim();
                if (!payload) continue;

                let event;
                try {
                    event = JSON.parse(payload);
                } catch {
                    continue;
                }
                handleEvent(event);
            }
        }

        // Stream closed = turn complete. Anything still buffered without a
        // trailing newline is a final `data:` line the loop never saw.
        const tail = buffer.trim();
        if (tail.startsWith("data:")) {
            try {
                handleEvent(JSON.parse(tail.slice("data:".length).trim()));
            } catch {
                /* ignore a truncated trailing line */
            }
        }

        handlers.onDone({ blocks });
    } catch (error) {
        handlers.onError(error instanceof Error ? error : new Error(String(error)));
    }
}
